import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import {
  accentMaterial,
  bodyMaterial,
  createMotes,
  createPedestal,
  glowMaterial,
  PortalScene,
  SceneFactory,
} from './portal-scene';

function createCrate(accent: THREE.Color, size: number): THREE.Group {
  const crate = new THREE.Group();

  const box = new THREE.Mesh(new RoundedBoxGeometry(size, size * 0.72, size, 5, 0.07), bodyMaterial(0x4a59a4));
  crate.add(box);

  const tape = new THREE.Mesh(new THREE.BoxGeometry(size * 1.01, size * 0.1, size * 0.18), accentMaterial(accent, 0.8));
  tape.position.y = size * 0.31;
  crate.add(tape);

  const label = new THREE.Mesh(new THREE.BoxGeometry(size * 0.34, size * 0.24, 0.02), accentMaterial(accent, 1.2));
  label.position.set(0, 0, size * 0.51);
  crate.add(label);

  return crate;
}

/** Proveedores: stacked supply crates with a delivery arrow circling them. */
export const createProveedoresScene: SceneFactory = (accent): PortalScene => {
  const root = new THREE.Group();

  const pivot = new THREE.Group();
  root.add(pivot);

  const pallet = new THREE.Mesh(new RoundedBoxGeometry(2.3, 0.16, 1.7, 5, 0.05), bodyMaterial(0x2b3670));
  pallet.position.y = -1.08;
  pivot.add(pallet);

  const stack: Array<{ crate: THREE.Group; baseY: number }> = [];

  const bottom = createCrate(accent, 1.15);
  bottom.position.set(-0.3, -0.58, 0);
  bottom.rotation.y = 0.22;
  pivot.add(bottom);
  stack.push({ crate: bottom, baseY: -0.58 });

  const middle = createCrate(accent, 0.92);
  middle.position.set(0.45, -0.22, 0.25);
  middle.rotation.y = -0.38;
  pivot.add(middle);
  stack.push({ crate: middle, baseY: -0.22 });

  const top = createCrate(accent, 0.78);
  top.position.set(-0.16, 0.36, -0.05);
  top.rotation.y = 0.52;
  pivot.add(top);
  stack.push({ crate: top, baseY: 0.36 });

  const arrowOrbit = new THREE.Group();
  const arrow = new THREE.Group();

  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.52, 20), accentMaterial(accent, 1.1));
  shaft.rotation.z = Math.PI / 2;
  arrow.add(shaft);

  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.15, 0.32, 24), accentMaterial(accent, 1.1));
  tip.rotation.z = -Math.PI / 2;
  tip.position.x = 0.4;
  arrow.add(tip);

  arrow.position.set(1.45, 1.08, 0);
  arrowOrbit.add(arrow);
  pivot.add(arrowOrbit);

  const route = new THREE.Mesh(new THREE.TorusGeometry(1.45, 0.016, 12, 110), glowMaterial(accent, 0.55));
  route.rotation.x = Math.PI / 2;
  route.position.y = 1.08;
  pivot.add(route);

  const motes = createMotes(accent, 13, 2.05);
  root.add(motes, createPedestal(accent));

  return {
    root,
    update(elapsed, hover) {
      pivot.position.y = Math.sin(elapsed * 1.0) * 0.06 + hover * 0.1;

      stack.forEach(({ crate, baseY }, index) => {
        crate.position.y = baseY + Math.sin(elapsed * 1.3 + index * 0.9) * 0.05;
        crate.rotation.y += 0.0022 + hover * 0.004;
      });

      arrowOrbit.rotation.y = elapsed * (0.7 + hover * 1.5);
      arrow.rotation.y = Math.sin(elapsed * 2) * 0.1;
      route.rotation.z = elapsed * 0.3;
      motes.rotation.y = -elapsed * 0.12;
    },
  };
};
